import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  HandHeart,
  ShieldCheck,
  Loader2,
  Save,
  XCircle,
  Lock,
  Info,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/auth";

/**
 * EA-1 Slice 2 — Operator Assist Panel.
 *
 * Renders inline beneath the IntakeSpecialist detail card. The panel
 * consumes the EA-1 Slice 1 backend contract exclusively:
 *   POST /api/ops/assist/consent
 *   GET  /api/ops/assist/consent/active?patient_user_id=
 *   POST /api/ops/assist/consent/{cid}/withdraw
 *   POST /api/intake/specialist/{intake_id}/assisted-answers
 *
 * The frontend is NOT an authorization boundary; the backend remains
 * authoritative. This component performs no Northstar activation,
 * no intake submission, no status transition, and no import of
 * AI/Northstar/Journey/Personalization/Buddy modules.
 */

const CHANNELS = [
  { v: "in_person", l: "In person" },
  { v: "phone", l: "Phone" },
  { v: "remote_shared_screen", l: "Shared screen" },
];

const JOURNEY_TYPES = [
  { v: "patient", l: "Patient" },
  { v: "survivor", l: "Survivor" },
  { v: "caregiver", l: "Carer" },
];

const JOURNEY_STAGES = {
  patient: [
    { v: "newly_diagnosed", l: "Newly diagnosed" },
    { v: "in_treatment", l: "In treatment" },
    { v: "post_treatment", l: "Just finished treatment" },
    { v: "advanced", l: "Living with advanced cancer" },
  ],
  survivor: [
    { v: "recent", l: "Under 1 year post-treatment" },
    { v: "one_to_five", l: "1 to 5 years post-treatment" },
    { v: "long_term", l: "5+ years post-treatment" },
  ],
  caregiver: [
    { v: "supporting_active", l: "Supporting an active patient" },
    { v: "supporting_survivor", l: "Supporting a survivor" },
    { v: "grieving", l: "Grieving a loved one" },
  ],
};

const SUPPORT_NEEDS = [
  { v: "emotional", l: "Emotional support" },
  { v: "practical", l: "Practical everyday help" },
  { v: "information", l: "Trusted information & guidance" },
  { v: "community", l: "Community & connection" },
  { v: "financial", l: "Financial guidance" },
  { v: "carer_respite", l: "Carer respite" },
];

const AVAILABILITY = [
  { v: "weekday_mornings", l: "Weekday mornings" },
  { v: "weekday_afternoons", l: "Weekday afternoons" },
  { v: "weekday_evenings", l: "Weekday evenings" },
  { v: "weekends", l: "Weekends" },
];

const COMMS = [
  { v: "email", l: "Email" },
  { v: "chat", l: "In-portal chat" },
  { v: "phone", l: "Phone call" },
  { v: "video", l: "Video call" },
];

const BUDDY_PREF_KEYS = [
  { k: "similar_diagnosis", l: "Similar diagnosis" },
  { k: "similar_age", l: "Similar age" },
  { k: "same_language", l: "Same first language" },
];

function toggle(list, v) {
  const set = new Set(list || []);
  if (set.has(v)) set.delete(v); else set.add(v);
  return Array.from(set);
}

function formatExpiration(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function toReadableError(err, fallback) {
  const status = err?.response?.status;
  const detail = formatApiErrorDetail(err?.response?.data?.detail);
  if (status === 401) return "Your session expired. Please sign in again.";
  if (status === 403) return detail || "You are not authorized for this action.";
  if (status === 404) return detail || "This record could not be found.";
  if (status === 409) return detail || "This action conflicts with the current state.";
  if (status === 422) return detail || "The request was invalid.";
  return detail || fallback || "Something went wrong. Please try again.";
}

export default function AssistPanel({ intake, onIntakeRefreshed }) {
  const patientUserId = intake?.user_id;
  const intakeId = intake?.id;
  const intakeStatus = intake?.status;

  const [consent, setConsent] = useState(null); // active consent row or null
  const [checking, setChecking] = useState(true);

  // Grant-form state.
  const [confirmed, setConfirmed] = useState(false);
  const [channel, setChannel] = useState("in_person");
  const [granting, setGranting] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  // Assisted-answer form state (seeded from intake).
  const [form, setForm] = useState({
    journey_type: intake?.journey_type || "",
    journey_stage: intake?.journey_stage || "",
    support_needs: intake?.support_needs || [],
    language: intake?.language || "",
    location: intake?.location || "",
    communication_preference: intake?.communication_preference || "",
    availability: intake?.availability || [],
    buddy_preferences: intake?.buddy_preferences || {
      similar_diagnosis: false,
      similar_age: false,
      same_language: true,
    },
  });
  const [saving, setSaving] = useState(false);

  // Reset local form whenever intake changes underneath.
  useEffect(() => {
    setForm({
      journey_type: intake?.journey_type || "",
      journey_stage: intake?.journey_stage || "",
      support_needs: intake?.support_needs || [],
      language: intake?.language || "",
      location: intake?.location || "",
      communication_preference: intake?.communication_preference || "",
      availability: intake?.availability || [],
      buddy_preferences: intake?.buddy_preferences || {
        similar_diagnosis: false,
        similar_age: false,
        same_language: true,
      },
    });
  }, [
    intake?.id,
    intake?.journey_type,
    intake?.journey_stage,
    intake?.language,
    intake?.location,
    intake?.communication_preference,
  ]);

  const refreshConsent = useCallback(async () => {
    if (!patientUserId) return;
    setChecking(true);
    try {
      const { data } = await api.get("/ops/assist/consent/active", {
        params: { patient_user_id: patientUserId },
      });
      setConsent(data?.active ? data.consent : null);
    } catch (err) {
      setConsent(null);
    } finally {
      setChecking(false);
    }
  }, [patientUserId]);

  useEffect(() => {
    refreshConsent();
  }, [refreshConsent]);

  const isDraft = intakeStatus === "draft";
  const active = !!consent;
  const canEdit = active && isDraft;

  const grantConsent = async () => {
    if (!confirmed) return;
    setGranting(true);
    try {
      const { data } = await api.post("/ops/assist/consent", {
        patient_user_id: patientUserId,
        channel,
        scope: "intake_answers",
        verification_method: "patient_present",
      });
      setConsent(data);
      setConfirmed(false);
      toast.success("Assisted session started. The patient still controls final submission.");
    } catch (err) {
      toast.error(toReadableError(err, "Could not start assisted session."));
      await refreshConsent();
    } finally {
      setGranting(false);
    }
  };

  const withdraw = async () => {
    if (!consent?.id) return;
    setWithdrawing(true);
    try {
      await api.post(`/ops/assist/consent/${consent.id}/withdraw`);
      toast.success("Assistance ended. The patient's information remains as saved.");
      setConsent(null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409 || status === 403 || status === 404) {
        // Session already withdrawn/expired server-side — refresh silently.
        toast.info("Assistance session already ended.");
      } else {
        toast.error(toReadableError(err, "Could not withdraw assistance."));
      }
      await refreshConsent();
    } finally {
      setWithdrawing(false);
    }
  };

  const buildPayload = () => {
    // Only include fields the operator has actually entered a value for.
    const p = {};
    if (form.journey_type) p.journey_type = form.journey_type;
    if (form.journey_stage) p.journey_stage = form.journey_stage;
    if (Array.isArray(form.support_needs)) p.support_needs = form.support_needs;
    if (form.language) p.language = form.language;
    if (form.location) p.location = form.location;
    if (form.communication_preference) p.communication_preference = form.communication_preference;
    if (Array.isArray(form.availability)) p.availability = form.availability;
    if (form.buddy_preferences && typeof form.buddy_preferences === "object") {
      p.buddy_preferences = form.buddy_preferences;
    }
    return p;
  };

  const save = async () => {
    if (!canEdit || !intakeId) return;
    setSaving(true);
    try {
      const payload = buildPayload();
      const { data } = await api.post(
        `/intake/specialist/${intakeId}/assisted-answers`,
        payload
      );
      toast.success("Information saved. The patient still controls final submission.");
      if (data?.intake && onIntakeRefreshed) onIntakeRefreshed(data.intake);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403 || status === 409) {
        // Consent expired/withdrawn or intake left draft — re-sync state.
        toast.error(toReadableError(err, "Save failed."));
        await refreshConsent();
      } else {
        toast.error(toReadableError(err, "Save failed."));
      }
    } finally {
      setSaving(false);
    }
  };

  const stages = form.journey_type ? JOURNEY_STAGES[form.journey_type] || [] : [];

  return (
    <section
      className="mt-8 rounded-2xl border border-[#F5A524]/40 bg-white overflow-hidden"
      data-testid="assist-panel"
      aria-labelledby="assist-panel-heading"
    >
      {/* Header */}
      <header className="px-6 py-5 bg-[#FFF8EC] border-b border-[#F5A524]/30 flex items-start gap-4">
        <div className="mt-1 shrink-0 w-9 h-9 rounded-full bg-[#F5A524]/20 flex items-center justify-center">
          <HandHeart className="w-5 h-5 text-[#B45309]" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#B45309]">
            Assisted Access
          </p>
          <h3
            id="assist-panel-heading"
            className="font-extrabold text-lg text-[#0B132B]"
          >
            Help this member enter their intake
          </h3>
          <p className="text-sm text-[#4B5563] mt-1 max-w-2xl">
            You may enter draft answers on the patient's behalf while an
            active assisted session exists. You cannot submit the intake or
            grant portal access — the patient controls final submission.
          </p>
        </div>
      </header>

      {/* Body */}
      <div className="px-6 py-6" aria-live="polite">
        {checking ? (
          <div
            className="flex items-center gap-2 text-sm text-[#4B5563]"
            data-testid="assist-loading"
          >
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            Checking consent…
          </div>
        ) : !active ? (
          /* -------------------- INACTIVE STATE -------------------- */
          <div className="space-y-5" data-testid="assist-inactive">
            <div className="flex items-start gap-3 rounded-xl bg-[#F8F9FB] border border-gray-100 px-4 py-3">
              <Info className="w-4 h-4 text-[#4B5563] mt-0.5 shrink-0" aria-hidden="true" />
              <div className="text-sm text-[#0B132B]">
                <span className="font-semibold" data-testid="assist-state-label">
                  Assisted access is not active.
                </span>{" "}
                Start a session with the patient's explicit consent to enter
                information on their behalf.
              </div>
            </div>

            {/* Channel selector */}
            <fieldset className="space-y-2">
              <legend className="text-[11px] uppercase tracking-wider font-semibold text-[#4B5563]">
                How is the patient with you?
              </legend>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Assistance channel">
                {CHANNELS.map((c) => (
                  <button
                    key={c.v}
                    type="button"
                    role="radio"
                    aria-checked={channel === c.v}
                    data-testid={`assist-channel-${c.v}`}
                    onClick={() => setChannel(c.v)}
                    className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                      channel === c.v
                        ? "bg-[#0B132B] text-white border-[#0B132B]"
                        : "bg-white text-[#0B132B] border-gray-200 hover:border-[#0B132B]"
                    }`}
                  >
                    {c.l}
                  </button>
                ))}
              </div>
              <p className="text-xs text-[#4B5563]">
                The channel is descriptive only. It does not verify identity.
              </p>
            </fieldset>

            {/* Confirmation checkbox */}
            <label className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="assist-consent-confirm"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#2D9B4E]"
              />
              <span className="text-sm text-[#0B132B]">
                <span className="font-semibold">
                  Patient is present and has authorized this assistance
                  session.
                </span>{" "}
                Consent is limited to entering draft intake answers and
                automatically expires within 24 hours.
              </span>
            </label>

            <div>
              <button
                type="button"
                onClick={grantConsent}
                disabled={!confirmed || granting || !patientUserId}
                data-testid="assist-grant-button"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#0B132B] text-white text-sm font-semibold hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {granting ? (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                ) : (
                  <ShieldCheck className="w-4 h-4" aria-hidden="true" />
                )}
                Start Assisted Session
              </button>
            </div>
          </div>
        ) : (
          /* -------------------- ACTIVE STATE -------------------- */
          <div className="space-y-6" data-testid="assist-active">
            <div className="flex flex-wrap items-start gap-3 rounded-xl bg-[#2D9B4E]/10 border border-[#2D9B4E]/40 px-4 py-3">
              <ShieldCheck className="w-4 h-4 text-[#23803F] mt-0.5 shrink-0" aria-hidden="true" />
              <div className="text-sm text-[#0B132B] flex-1 min-w-0">
                <div className="font-semibold" data-testid="assist-state-label">
                  Assisted access is active.
                </div>
                <dl className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                  <div>
                    <dt className="text-[#4B5563] uppercase tracking-wider">Channel</dt>
                    <dd className="font-semibold capitalize" data-testid="assist-channel-label">
                      {(consent.channel || "").replace(/_/g, " ")}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[#4B5563] uppercase tracking-wider">Scope</dt>
                    <dd className="font-semibold" data-testid="assist-scope-label">
                      Draft intake answers
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[#4B5563] uppercase tracking-wider">Expires</dt>
                    <dd className="font-semibold" data-testid="assist-expires-label">
                      {formatExpiration(consent.expires_at)}
                    </dd>
                  </div>
                </dl>
              </div>
              <button
                type="button"
                onClick={withdraw}
                disabled={withdrawing}
                data-testid="assist-withdraw-button"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#B45309]/60 bg-white text-[#B45309] text-xs font-semibold hover:bg-[#FFF8EC] disabled:opacity-50 transition"
              >
                {withdrawing ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <XCircle className="w-3.5 h-3.5" aria-hidden="true" />
                )}
                Withdraw Assistance
              </button>
            </div>

            {!isDraft && (
              <div
                className="flex items-start gap-3 rounded-xl bg-[#F8F9FB] border border-gray-200 px-4 py-3"
                data-testid="assist-not-draft-notice"
              >
                <Lock className="w-4 h-4 text-[#4B5563] mt-0.5 shrink-0" aria-hidden="true" />
                <div className="text-sm text-[#0B132B]">
                  <span className="font-semibold">Intake is no longer a draft.</span>{" "}
                  Assisted editing is limited to draft intakes. The patient has
                  submitted their information.
                </div>
              </div>
            )}

            <AssistForm
              form={form}
              setForm={setForm}
              stages={stages}
              disabled={!canEdit}
            />

            <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={save}
                disabled={!canEdit || saving}
                data-testid="assist-save-button"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#2D9B4E] text-white text-sm font-semibold hover:bg-[#238040] disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Save className="w-4 h-4" aria-hidden="true" />
                )}
                Save Assisted Answers
              </button>
              <div className="text-xs text-[#4B5563] flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5 text-[#B45309]" aria-hidden="true" />
                Saving does not submit the intake. The patient still controls
                final submission.
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function AssistForm({ form, setForm, stages, disabled }) {
  const rowClass = "space-y-2";
  const labelClass =
    "text-[11px] uppercase tracking-wider font-semibold text-[#4B5563]";
  const inputClass = `w-full px-4 py-3 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:bg-white focus:border-[#2D9B4E] focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed`;

  return (
    <fieldset
      disabled={disabled}
      aria-disabled={disabled}
      data-testid="assist-form"
      className={disabled ? "opacity-60" : ""}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Journey type */}
        <div className={rowClass}>
          <label htmlFor="assist-journey-type" className={labelClass}>
            Journey
          </label>
          <select
            id="assist-journey-type"
            data-testid="assist-journey-type"
            value={form.journey_type || ""}
            onChange={(e) => setForm({ ...form, journey_type: e.target.value, journey_stage: "" })}
            className={inputClass}
          >
            <option value="">— select —</option>
            {JOURNEY_TYPES.map((j) => (
              <option key={j.v} value={j.v}>{j.l}</option>
            ))}
          </select>
        </div>

        {/* Journey stage */}
        <div className={rowClass}>
          <label htmlFor="assist-journey-stage" className={labelClass}>
            Stage
          </label>
          <select
            id="assist-journey-stage"
            data-testid="assist-journey-stage"
            value={form.journey_stage || ""}
            onChange={(e) => setForm({ ...form, journey_stage: e.target.value })}
            className={inputClass}
          >
            <option value="">— select —</option>
            {stages.map((s) => (
              <option key={s.v} value={s.v}>{s.l}</option>
            ))}
          </select>
        </div>

        {/* Language */}
        <div className={rowClass}>
          <label htmlFor="assist-language" className={labelClass}>Language</label>
          <input
            id="assist-language"
            data-testid="assist-language"
            type="text"
            value={form.language || ""}
            onChange={(e) => setForm({ ...form, language: e.target.value })}
            className={inputClass}
            placeholder="e.g. English"
          />
        </div>

        {/* Location */}
        <div className={rowClass}>
          <label htmlFor="assist-location" className={labelClass}>Location</label>
          <input
            id="assist-location"
            data-testid="assist-location"
            type="text"
            value={form.location || ""}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
            className={inputClass}
            placeholder="City / region"
          />
        </div>

        {/* Communication preference */}
        <div className={rowClass}>
          <label htmlFor="assist-comm" className={labelClass}>
            Communication preference
          </label>
          <select
            id="assist-comm"
            data-testid="assist-comm"
            value={form.communication_preference || ""}
            onChange={(e) => setForm({ ...form, communication_preference: e.target.value })}
            className={inputClass}
          >
            <option value="">— select —</option>
            {COMMS.map((c) => (
              <option key={c.v} value={c.v}>{c.l}</option>
            ))}
          </select>
        </div>

        {/* Support needs */}
        <div className={`${rowClass} md:col-span-2`}>
          <span className={labelClass}>Support needs</span>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Support needs">
            {SUPPORT_NEEDS.map((s) => {
              const on = (form.support_needs || []).includes(s.v);
              return (
                <button
                  key={s.v}
                  type="button"
                  aria-pressed={on}
                  data-testid={`assist-need-${s.v}`}
                  onClick={() =>
                    setForm({ ...form, support_needs: toggle(form.support_needs, s.v) })
                  }
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    on
                      ? "bg-[#2D9B4E] text-white border-[#2D9B4E]"
                      : "bg-white text-[#0B132B] border-gray-200 hover:border-[#2D9B4E]"
                  } disabled:cursor-not-allowed`}
                >
                  {s.l}
                </button>
              );
            })}
          </div>
        </div>

        {/* Availability */}
        <div className={`${rowClass} md:col-span-2`}>
          <span className={labelClass}>Availability</span>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Availability">
            {AVAILABILITY.map((a) => {
              const on = (form.availability || []).includes(a.v);
              return (
                <button
                  key={a.v}
                  type="button"
                  aria-pressed={on}
                  data-testid={`assist-avail-${a.v}`}
                  onClick={() =>
                    setForm({ ...form, availability: toggle(form.availability, a.v) })
                  }
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    on
                      ? "bg-[#1A5FAB] text-white border-[#1A5FAB]"
                      : "bg-white text-[#0B132B] border-gray-200 hover:border-[#1A5FAB]"
                  } disabled:cursor-not-allowed`}
                >
                  {a.l}
                </button>
              );
            })}
          </div>
        </div>

        {/* Buddy preferences */}
        <div className={`${rowClass} md:col-span-2`}>
          <span className={labelClass}>Buddy preferences</span>
          <div className="flex flex-wrap gap-4 pt-1">
            {BUDDY_PREF_KEYS.map((b) => (
              <label
                key={b.k}
                className="inline-flex items-center gap-2 text-sm text-[#0B132B]"
              >
                <input
                  type="checkbox"
                  data-testid={`assist-buddy-${b.k}`}
                  checked={!!form.buddy_preferences?.[b.k]}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      buddy_preferences: {
                        ...(form.buddy_preferences || {}),
                        [b.k]: e.target.checked,
                      },
                    })
                  }
                  className="w-4 h-4 accent-[#2D9B4E]"
                />
                {b.l}
              </label>
            ))}
          </div>
        </div>
      </div>
    </fieldset>
  );
}
